/**
 * Central utility for option pricing calculations
 */

/**
 * Calculate intrinsic value of an option
 * @param optionType - 'call' or 'put'
 * @param strikePrice - Strike price of the option
 * @param underlyingPrice - Current price of the underlying
 * @returns Intrinsic value (always >= 0)
 */
export function calculateIntrinsicValue(
  optionType: 'call' | 'put' | 'C' | 'P',
  strikePrice: number,
  underlyingPrice: number
): number {
  if (!strikePrice || !underlyingPrice) return 0;
  
  const type = optionType === 'C' ? 'call' : optionType === 'P' ? 'put' : optionType;
  
  if (type === 'call') {
    return Math.max(0, underlyingPrice - strikePrice);
  } else {
    return Math.max(0, strikePrice - underlyingPrice);
  }
}

/**
 * Calculate extrinsic value of an option
 * Extrinsic Value = Option Price - Intrinsic Value
 * @param optionPrice - Current market price of the option (mark/mid price)
 * @param intrinsicValue - Intrinsic value of the option
 * @returns Extrinsic value (time value + volatility value)
 */
export function calculateExtrinsicValue(
  optionPrice: number,
  intrinsicValue: number
): number {
  if (!optionPrice || optionPrice <= 0) return 0;
  
  // Extrinsic value is what's left after intrinsic value
  // It represents time value and implied volatility premium
  const extrinsic = optionPrice - intrinsicValue;
  
  // Extrinsic value should never be negative
  // If it is, it means the option is trading below intrinsic (rare but possible)
  return Math.max(0, extrinsic);
}

/**
 * Calculate both intrinsic and extrinsic values
 * @param optionType - 'call' or 'put'
 * @param strikePrice - Strike price of the option
 * @param underlyingPrice - Current price of the underlying
 * @param optionPrice - Current market price of the option
 * @returns Object with intrinsic and extrinsic values
 */
export function calculateOptionValues(
  optionType: 'call' | 'put' | 'C' | 'P',
  strikePrice: number,
  underlyingPrice: number,
  optionPrice: number
): {
  intrinsicValue: number;
  extrinsicValue: number;
  isITM: boolean;
  isATM: boolean;
  isOTM: boolean;
} {
  const intrinsicValue = calculateIntrinsicValue(optionType, strikePrice, underlyingPrice);
  const extrinsicValue = calculateExtrinsicValue(optionPrice, intrinsicValue);
  
  const type = optionType === 'C' ? 'call' : optionType === 'P' ? 'put' : optionType;
  
  // Determine moneyness
  const atmThreshold = underlyingPrice * 0.01; // 1% of underlying price
  const isATM = Math.abs(strikePrice - underlyingPrice) <= atmThreshold;
  
  let isITM = false;
  let isOTM = false;
  
  if (!isATM) {
    if (type === 'call') {
      isITM = underlyingPrice > strikePrice;
      isOTM = underlyingPrice < strikePrice;
    } else {
      isITM = underlyingPrice < strikePrice;
      isOTM = underlyingPrice > strikePrice;
    }
  }
  
  return {
    intrinsicValue,
    extrinsicValue,
    isITM,
    isATM,
    isOTM
  };
}

/**
 * Format extrinsic value for display
 * @param extrinsicValue - The extrinsic value to format (per share)
 * @param multiplier - Contract multiplier (typically 100 for options)
 * @param quantity - Number of contracts (optional, defaults to 1)
 * @returns Formatted string for display
 */
export function formatExtrinsicValue(extrinsicValue: number, multiplier: number = 100, quantity: number = 1): string {
  if (!extrinsicValue || extrinsicValue === 0) return '$0';
  
  // Calculate total extrinsic value for the position
  const totalValue = extrinsicValue * multiplier * Math.abs(quantity);
  
  if (totalValue >= 1000) {
    return `$${(totalValue / 1000).toFixed(1)}k`;
  }
  
  return `$${totalValue.toFixed(0)}`;
}